import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { isTextInputElement } from '@client/helpers/dom.helpers';

/**
 * True when keyboard shortcuts should not steal focus from form fields.
 */
export const isDesignCanvasTypingTarget = (
  target: EventTarget | null,
): boolean => {
  if (!(target instanceof Element)) return false;
  if (isTextInputElement(target)) return true;
  return target instanceof HTMLElement && target.tagName === 'SELECT';
};

/** Roles whose keyboard contract includes Space activation. */
const SPACE_ACTIVATED_ROLES = [
  '[role="button"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
].join(',');

/**
 * True when Space already has a native meaning for {@link target}, so the
 * Design canvas must not repurpose it for panning.
 */
export const isSpaceActivatedTarget = (target: EventTarget | null): boolean => {
  if (isDesignCanvasTypingTarget(target)) return true;
  if (!(target instanceof HTMLElement)) return false;
  if (target.tagName === 'BUTTON') return true;
  return target.closest(SPACE_ACTIVATED_ROLES) !== null;
};

type AllowsDesignSpacePanOptions = {
  /**
   * When true (default), Space-pan is allowed if focus is on `document` /
   * `body` (Mapbox often leaves focus there). Wizard previews should pass
   * false so Space still activates dialog buttons.
   */
  allowDocumentRoot?: boolean;
};

/**
 * True when Space should pan a Design surface instead of activating chrome.
 */
export const allowsDesignSpacePan = (
  target: EventTarget | null,
  container: Node | null | undefined,
  options: AllowsDesignSpacePanOptions = {},
): boolean => {
  if (isSpaceActivatedTarget(target)) return false;
  if (options.allowDocumentRoot !== false) {
    if (target === document || target === document.body) return true;
  }
  if (!container) return false;
  return target instanceof Node && container.contains(target);
};

/**
 * Sort placed underlays in page order for toolbar prev/next.
 */
export const sortBlueprintUnderlaysByPage = (
  underlays: readonly BlueprintUnderlay[],
): BlueprintUnderlay[] =>
  [...underlays].sort(
    (left, right) =>
      left.pageIndex - right.pageIndex || left.id.localeCompare(right.id),
  );

/**
 * Resolve the adjacent placed PDF page id (wraps at ends).
 */
export const getAdjacentBlueprintUnderlayId = (
  underlays: readonly BlueprintUnderlay[],
  activeId: string,
  direction: -1 | 1,
): string | undefined => {
  if (underlays.length < 2) return undefined;
  const ordered = sortBlueprintUnderlaysByPage(underlays);
  const index = ordered.findIndex((page) => page.id === activeId);
  if (index < 0) return undefined;
  const next = ordered[(index + direction + ordered.length) % ordered.length];
  return next?.id;
};

/** Mapbox zoom treated as 100% on the Design canvas zoom chip. */
const DESIGN_MAP_ZOOM_AT_100_PERCENT = 17;

/**
 * Format Mapbox zoom as a whole-number percent relative to a tracing baseline.
 */
export const formatDesignMapZoomPercent = (zoom: number): string => {
  if (!Number.isFinite(zoom)) return '—';
  const percent = Math.round(
    100 * 2 ** (zoom - DESIGN_MAP_ZOOM_AT_100_PERCENT),
  );
  return `${Math.max(1, percent)}%`;
};
